import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Phone, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import SectionHeader from '@/components/shared/SectionHeader';
import { useSite } from '@/context/SiteContext';
import ContactForm from '@/components/forms/ContactForm';

const ContactInfoSection = () => {
    const { settings } = useSite();
    if (!settings) return null;
    return (
        <section
            className="section-pad bg-[hsl(var(--secondary))]/60 relative overflow-hidden"
            data-testid="home-contact-section"
        >
            <div className="absolute inset-0 grain-bg opacity-60 pointer-events-none" />
            <div className="container-page relative">
                <SectionHeader
                    eyebrow="Contact"
                    title="Get in touch with us."
                    description="Head office in Puducherry, branch office in Chennai. Reach out for programs, partnerships or support."
                />
                <div className="mt-10 grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5 space-y-4">
                        {(settings.offices || []).map((o, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.06 }}
                                viewport={{ once: true, margin: '-60px' }}
                            >
                                <Card className="bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-2 text-[hsl(var(--accent))] mb-3">
                                            <MapPin className="h-4 w-4" />
                                            <span className="text-xs font-semibold uppercase tracking-widest">
                                                {o.label}
                                            </span>
                                        </div>
                                        <div className="text-sm text-foreground/80 leading-relaxed">
                                            {o.address_lines.join(', ')}
                                            <br />
                                            {o.city}, {o.state} - {o.pincode}
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
                                            {o.phone && (
                                                <a
                                                    href={`tel:${o.phone}`}
                                                    className="flex items-center gap-1 text-[hsl(var(--primary))] font-medium"
                                                >
                                                    <Phone className="h-3.5 w-3.5" /> {o.phone}
                                                </a>
                                            )}
                                            {o.email && (
                                                <a
                                                    href={`mailto:${o.email}`}
                                                    className="flex items-center gap-1 text-[hsl(var(--primary))] font-medium"
                                                >
                                                    <Mail className="h-3.5 w-3.5" /> {o.email}
                                                </a>
                                            )}
                                        </div>
                                        {o.map_embed_url && (
                                            <div className="mt-3 aspect-video w-full rounded-lg overflow-hidden border border-border">
                                                <iframe
                                                    src={o.map_embed_url}
                                                    title={`Map — ${o.label}`}
                                                    className="w-full h-full"
                                                    loading="lazy"
                                                    data-testid={`contact-map-${i}`}
                                                />
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                    <div className="lg:col-span-7">
                        <Card className="bg-card border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
                            <CardContent className="p-6">
                                <ContactForm />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactInfoSection;
